import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

// Context to share dialog state with DialogContent
const DialogStateContext = React.createContext<{
	isOpen: boolean;
	dialogId: string;
} | null>(null);

function Dialog({
	open,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
	const dialogId = React.useMemo(() => Math.random().toString(36), []);

	return (
		<DialogStateContext.Provider value={{ isOpen: Boolean(open), dialogId }}>
			<DialogPrimitive.Root data-slot="dialog" open={open} {...props} />
		</DialogStateContext.Provider>
	);
}

function DialogTrigger({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = React.forwardRef<
	React.ComponentRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		data-slot="dialog-overlay"
		className={cn(
			'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]',
			className
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type DialogSize = 'sm' | 'md' | 'lg';

const dialogSizeClasses: Record<DialogSize, string> = {
	sm: 'sm:max-w-lg',      // 512px
	md: 'sm:max-w-2xl',     // 672px
	lg: 'sm:max-w-4xl',     // 896px
};

function DialogContent({
	className,
	children,
	showCloseButton = true,
	enablePreRender = true,
	preRenderTimeout = 300,
	size,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
	showCloseButton?: boolean;
	enablePreRender?: boolean;
	preRenderTimeout?: number;
	size?: DialogSize;
}) {
	const dialogState = React.useContext(DialogStateContext);
	const [isContentReady, setIsContentReady] = React.useState(!enablePreRender);
	const contentRef = React.useRef<HTMLDivElement>(null);
	const isOpen = dialogState?.isOpen ?? true;
	const shouldPlayOpenAnimation = isOpen && isContentReady;

	// Pre-render content with timeout to prevent jitter on open
	React.useEffect(() => {
		if (!enablePreRender) {
			setIsContentReady(true);
			return;
		}

		const isOpen = dialogState?.isOpen ?? true;

		if (isOpen) {
			const timer = setTimeout(() => {
				setIsContentReady(true);
			}, preRenderTimeout);

			return () => {
				clearTimeout(timer);
			};
		} else {
			setIsContentReady(false);
		}
	}, [enablePreRender, dialogState?.isOpen, preRenderTimeout]);

	return (
		<DialogPortal data-slot="dialog-portal">
			<DialogOverlay />
			<div className="fixed inset-0 z-50 flex items-stretch justify-stretch sm:items-center sm:justify-center">
				<DialogPrimitive.Content
					ref={contentRef}
					data-slot="dialog-content"
					className={cn(
						// Base styles
						'relative bg-white dark:bg-slate-900 grid w-full origin-bottom border shadow-xl',
						// Mobile: full-screen (no size constraints)
						'h-full rounded-none max-w-none max-h-none',
						// Desktop: centered modal with size
						'sm:h-auto sm:rounded-xl sm:max-h-[85vh]',
						// Apply size variant on desktop (only above mobile)
						size ? dialogSizeClasses[size] : 'sm:max-w-[98vw]',
						// Padding
						'p-6',
						// Animations
						'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom-6 sm:data-[state=closed]:slide-out-to-bottom-4 sm:data-[state=closed]:zoom-out-[0.98] duration-200 ease-in',
						shouldPlayOpenAnimation &&
							'animate-in fade-in-0 slide-in-from-bottom-6 sm:slide-in-from-bottom-4 sm:zoom-in-[0.98] duration-300 ease-out',
						className
					)}
					style={{
						...(enablePreRender && !isContentReady ? {
							opacity: '0',
							pointerEvents: 'none'
						} : {})
					}}
					{...props}
				>
					{children}
					{showCloseButton && (
						<DialogPrimitive.Close
							data-slot="dialog-close"
							className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
						>
							<XIcon />
							<span className="sr-only">Close</span>
						</DialogPrimitive.Close>
					)}
				</DialogPrimitive.Content>
			</div>
		</DialogPortal>
	);
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-header"
			className={cn(
				'flex flex-col gap-2 text-center sm:text-left',
				className
			)}
			{...props}
		/>
	);
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn(
				'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
				className
			)}
			{...props}
		/>
	);
}

function DialogTitle({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="dialog-title"
			className={cn('text-lg leading-none font-semibold', className)}
			{...props}
		/>
	);
}

function DialogDescription({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot="dialog-description"
			className={cn('text-muted-foreground text-sm', className)}
			{...props}
		/>
	);
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger
};
